import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../../contexts/AuthContext';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Card, CardContent } from '../../components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Badge } from '../../components/ui/badge';
import { ArrowLeft, Upload, X, Star } from 'lucide-react';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function ProductForm() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { token } = useAuth();
  const headers = { Authorization: `Bearer ${token}` };
  const isEdit = !!id && id !== 'nuevo';
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(false);
  const [form, setForm] = useState({
    sku: '', name: '', description: '', category_id: '', subcategory_id: '',
    brand: '', price: 0, stock: 0, status: 'active', specs: '', internal_notes: ''
  });
  const [images, setImages] = useState([]);
  const [pdfFile, setPdfFile] = useState(null);
  const [currentPdfUrl, setCurrentPdfUrl] = useState(null);

  useEffect(() => {
    axios.get(`${API}/categories`).then(r => setCategories(r.data)).catch(() => {});
    if (isEdit) {
      axios.get(`${API}/products/${id}?include_inactive=true`).then(r => {
        const p = r.data;
        setForm({ sku: p.sku, name: p.name, description: p.description || '', category_id: p.category_id || '', subcategory_id: p.subcategory_id || '', brand: p.brand || '', price: p.price, stock: p.stock, status: p.status, specs: p.specs || '', internal_notes: p.internal_notes || '' });
        setImages(p.images || []);
        if (p.pdf_url) {
          setCurrentPdfUrl(p.pdf_url);
        }
      }).catch(() => navigate('/admin/productos'));
    }
  }, [id, isEdit, navigate]);

  const parentCats = categories.filter(c => !c.parent_id);
  const subCats = categories.filter(c => c.parent_id === form.category_id);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.sku || !form.name) { toast.error('SKU y nombre son obligatorios'); return; }
    setLoading(true);
    try {
      if (isEdit) {
        await axios.put(`${API}/products/${id}`, form, { headers });
        toast.success('Producto actualizado');
        
        // Subir PDF si hay uno nuevo
        if (pdfFile) {
          const formData = new FormData();
          formData.append('file', pdfFile);
          await axios.post(`${API}/admin/products/${id}/pdf`, formData, { headers });
          toast.success('PDF subido correctamente');
        }
        
        // Eliminar PDF si se solicitó
        if (currentPdfUrl === null && !pdfFile) {
          try {
            await axios.delete(`${API}/admin/products/${id}/pdf`, { headers });
          } catch (err) {
            // Ignorar si no existe
          }
        }
      } else {
        const res = await axios.post(`${API}/products`, form, { headers });
        const newId = res.data.id;
        toast.success('Producto creado');
        
        // Subir PDF si hay uno
        if (pdfFile) {
          const formData = new FormData();
          formData.append('file', pdfFile);
          await axios.post(`${API}/admin/products/${newId}/pdf`, formData, { headers });
          toast.success('PDF subido correctamente');
        }
        
        navigate(`/admin/productos/${newId}`);
        return;
      }
      navigate('/admin/productos');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Error al guardar');
    } finally {
      setLoading(false);
    }
  };

  const handleImageUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!isEdit) { toast.error('Guarde el producto primero para subir imágenes'); return; }
    const formData = new FormData();
    formData.append('file', file);
    try {
      const res = await axios.post(`${API}/products/${id}/images`, formData, { headers: { ...headers, 'Content-Type': 'multipart/form-data' } });
      setImages(res.data.images || []);
      toast.success('Imagen subida');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Error al subir imagen');
    }
    e.target.value = '';
  };

  const handleDeleteImage = async (imageId) => {
    try {
      await axios.delete(`${API}/products/${id}/images/${imageId}`, { headers });
      setImages(images.filter(i => i.id !== imageId));
      toast.success('Imagen eliminada');
    } catch (err) {
      toast.error('Error al eliminar imagen');
    }
  };

  const handleSetPrimary = async (imageId) => {
    try {
      await axios.put(`${API}/products/${id}/images/${imageId}/primary`, {}, { headers });
      setImages(images.map(i => ({ ...i, is_primary: i.id === imageId })));
      toast.success('Imagen principal actualizada');
    } catch (err) { toast.error('Error'); }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-4">
        <Button variant="ghost" size="sm" onClick={() => navigate('/admin/productos')}><ArrowLeft className="h-4 w-4" /></Button>
        <h1 className="text-2xl font-display font-semibold">{isEdit ? 'Editar Producto' : 'Nuevo Producto'}</h1>
      </div>

      <form onSubmit={handleSubmit}>
        <div className="grid lg:grid-cols-3 gap-6">
          <div className="lg:col-span-2 space-y-6">
            <Card>
              <CardContent className="p-6 space-y-4">
                <h3 className="font-display font-semibold">Información Básica</h3>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2"><Label>SKU *</Label><Input value={form.sku} onChange={e => setForm({...form, sku: e.target.value})} placeholder="AC-M500" className="font-mono" /></div>
                  <div className="space-y-2"><Label>Marca</Label><Input value={form.brand} onChange={e => setForm({...form, brand: e.target.value})} placeholder="AudioCodes" /></div>
                </div>
                <div className="space-y-2"><Label>Nombre *</Label><Input value={form.name} onChange={e => setForm({...form, name: e.target.value})} placeholder="Nombre del producto" /></div>
                <div className="space-y-2"><Label>Descripción</Label><textarea value={form.description} onChange={e => setForm({...form, description: e.target.value})} rows={3} className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm" placeholder="Descripción del producto" /></div>
              </CardContent>
            </Card>

            <Card>
              <CardContent className="p-6 space-y-4">
                <h3 className="font-display font-semibold">Categorización</h3>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Categoría</Label>
                    <Select value={form.category_id} onValueChange={v => setForm({...form, category_id: v, subcategory_id: ''})}>
                      <SelectTrigger><SelectValue placeholder="Seleccionar" /></SelectTrigger>
                      <SelectContent>{parentCats.map(c => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}</SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label>Subcategoría</Label>
                    <Select value={form.subcategory_id} onValueChange={v => setForm({...form, subcategory_id: v})}>
                      <SelectTrigger><SelectValue placeholder="Seleccionar" /></SelectTrigger>
                      <SelectContent>{subCats.map(c => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}</SelectContent>
                    </Select>
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardContent className="p-6 space-y-4">
                <h3 className="font-display font-semibold">Especificaciones Técnicas</h3>
                <textarea value={form.specs} onChange={e => setForm({...form, specs: e.target.value})} rows={5} className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm font-mono" placeholder="Clave: Valor (una por línea)" />
                <div className="space-y-2"><Label>Notas Internas</Label><textarea value={form.internal_notes} onChange={e => setForm({...form, internal_notes: e.target.value})} rows={2} className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm" placeholder="Solo visible para administradores" /></div>
              </CardContent>
            </Card>

            <Card>
              <CardContent className="p-6 space-y-4">
                <h3 className="font-display font-semibold">Ficha Técnica (PDF)</h3>
                <div className="space-y-2">
                  <Label>Subir PDF</Label>
                  <input 
                    type="file" 
                    accept=".pdf,application/pdf"
                    onChange={(e) => {
                      const file = e.target.files?.[0];
                      if (file) {
                        if (file.size > 10 * 1024 * 1024) {
                          toast.error('El PDF no debe exceder 10MB');
                          return;
                        }
                        setPdfFile(file);
                      }
                    }}
                    className="block w-full text-sm text-muted-foreground file:mr-4 file:py-2 file:px-4 file:rounded-md file:border-0 file:text-sm file:font-semibold file:bg-secondary file:text-foreground hover:file:bg-secondary/80"
                  />
                  <p className="text-xs text-muted-foreground">Máximo 10MB. El cliente podrá descargar este archivo desde la ficha del producto.</p>
                  
                  {pdfFile && (
                    <div className="flex items-center gap-2 p-2 bg-secondary/50 rounded">
                      <span className="text-sm">📄 {pdfFile.name}</span>
                      <Button 
                        type="button" 
                        variant="ghost" 
                        size="sm" 
                        onClick={() => setPdfFile(null)}
                        className="ml-auto"
                      >
                        <X className="h-4 w-4" />
                      </Button>
                    </div>
                  )}
                  
                  {currentPdfUrl && !pdfFile && (
                    <div className="flex items-center gap-2 p-2 bg-secondary/50 rounded">
                      <span className="text-sm">📄 PDF actual</span>
                      <a 
                        href={`${process.env.REACT_APP_BACKEND_URL}${currentPdfUrl}`} 
                        target="_blank" 
                        rel="noopener noreferrer"
                        className="text-sm text-primary hover:underline"
                      >
                        Ver PDF
                      </a>
                      <Button 
                        type="button" 
                        variant="ghost" 
                        size="sm" 
                        onClick={() => setCurrentPdfUrl(null)}
                        className="ml-auto"
                      >
                        <X className="h-4 w-4" />
                      </Button>
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>
          </div>

          <div className="space-y-6">
            <Card>
              <CardContent className="p-6 space-y-4">
                <h3 className="font-display font-semibold">Precio y Stock</h3>
                <div className="space-y-2">
                  <Label>Precio (USD)</Label>
                  <div className="relative">
                    <span className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground">$</span>
                    <Input 
                      type="number" 
                      step="0.01" 
                      min="0" 
                      value={form.price} 
                      onChange={e => setForm({...form, price: parseFloat(e.target.value) || 0})}
                      className="pl-7"
                      placeholder="0.00"
                    />
                  </div>
                  <p className="text-xs text-muted-foreground">El precio se guardará en dólares (USD). Se convertirá automáticamente a MXN en la tienda.</p>
                </div>
                <div className="space-y-2"><Label>Stock</Label><Input type="number" min="0" value={form.stock} onChange={e => setForm({...form, stock: parseInt(e.target.value) || 0})} /></div>
                <div className="space-y-2">
                  <Label>Estado</Label>
                  <Select value={form.status} onValueChange={v => setForm({...form, status: v})}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="active">Activo</SelectItem>
                      <SelectItem value="inactive">Inactivo</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </CardContent>
            </Card>

            {isEdit && (
              <Card>
                <CardContent className="p-6 space-y-4">
                  <h3 className="font-display font-semibold">Imágenes ({images.length}/5)</h3>
                  <div className="grid grid-cols-2 gap-2">
                    {images.map(img => (
                      <div key={img.id} className="relative group">
                        <img src={`${process.env.REACT_APP_BACKEND_URL}${img.url}`} alt="" className="w-full aspect-square object-cover rounded-md border" />
                        {img.is_primary && <Badge className="absolute top-1 left-1 text-xs">Principal</Badge>}
                        <div className="absolute top-1 right-1 flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                          <button onClick={() => handleSetPrimary(img.id)} className="p-1 bg-card rounded shadow border"><Star className="h-3 w-3" /></button>
                          <button onClick={() => handleDeleteImage(img.id)} className="p-1 bg-card rounded shadow border"><X className="h-3 w-3 text-destructive" /></button>
                        </div>
                      </div>
                    ))}
                  </div>
                  {images.length < 5 && (
                    <label className="flex items-center justify-center border-2 border-dashed rounded-md py-4 cursor-pointer hover:bg-secondary/30 transition-colors duration-150">
                      <div className="text-center">
                        <Upload className="h-5 w-5 mx-auto text-muted-foreground mb-1" />
                        <p className="text-xs text-muted-foreground">JPG, PNG o WebP · hasta 15 MB</p>
                      </div>
                      <input type="file" accept="image/jpeg,image/png,image/webp" onChange={handleImageUpload} className="hidden" />
                    </label>
                  )}
                </CardContent>
              </Card>
            )}

            <Button type="submit" className="w-full" disabled={loading}>
              {loading ? 'Guardando...' : isEdit ? 'Actualizar Producto' : 'Crear Producto'}
            </Button>
          </div>
        </div>
      </form>
    </div>
  );
}
